import { type Point } from './PointScale';
// eslint-disable-next-line import/no-named-default
import type { default as Timeline, TimeValue, TimeDataItem } from '../Timeline';
import { EVENT_TYPE_MAP } from './EventHandler';

/** 数据项 */
export type DataItem = Pick<Point, 'type' | 'index'> & {
  time: string;
  originTime: TimeValue;
};

/**
 * 数据集管理
 */
export default class Dataset {
  /** 时间轴实例 */
  timeline: Timeline;

  /** 时间数据 */
  protected _timeData: TimeDataItem[] = [];

  /** 解析后的数据 */
  protected _data: DataItem[] = [];

  /** 数据项有效索引区间（播放区间） 开闭 [,) */
  protected _indexRange: [number, number] = [0, 0];

  /** 区间开关 */
  protected _isRange = false;

  /** 当前数据项索引 */
  protected _currentIndex = 0;

  constructor(timeline: Timeline, timeData: TimeDataItem[], indexRange?: [number, number]) {
    this.timeline = timeline;
    this.__verify(timeData);
    // deepClone
    this._timeData = [...timeData];
    if (typeof indexRange !== 'undefined') {
      this._indexRange = indexRange.slice() as [number, number];
      this._isRange = true;
    } else {
      this._indexRange = [0, this._timeData.length];
    }
  }

  /**
   * 验证数据合法性
   */
  private __verify(timeData: TimeDataItem[]) {
    if (
      !timeData ||
      timeData.length <= 0 ||
      timeData[0] === null ||
      typeof timeData[0] === 'undefined'
    ) {
      throw new Error(`invalid data: ${JSON.stringify(timeData)}`);
    }

    const isObj =
      typeof timeData[0] === 'object' && timeData[0] !== null && !Array.isArray(timeData[0]);

    if (isObj && (timeData[0].type !== 'main' || timeData[timeData.length - 1].type !== 'main')) {
      throw new Error('The first and last data must be "main" type.');
    }

    return this;
  }

  /**
   * 解析数据
   */
  parse() {
    const self = this;

    const isObj =
      typeof self._timeData[0] === 'object' &&
      self._timeData[0] !== null &&
      !Array.isArray(self._timeData[0]);

    this._data = self._timeData.map((item, index) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const originTime: DataItem['originTime'] = isObj ? item.value : item;
      const time = String(originTime);

      if (
        Number.isNaN(Number(originTime)) &&
        typeof originTime !== 'string' &&
        !(originTime instanceof String)
      ) {
        throw new Error(`wrong time format: ${originTime}, should be number or string.`);
      }

      if (isObj) {
        return {
          index,
          type: item.type as Point['type'],
          time,
          originTime
        };
      }

      return {
        index,
        type: 'main',
        time,
        originTime
      };
    });

    this.__setIndexRange(this._indexRange.slice() as [number, number]);
  }

  /**
   * 获取数据集
   */
  getData(range = true) {
    if (range === true) {
      // deepClone
      return this._data.slice(...this._indexRange);
    }

    // deepClone
    return this._data.slice();
  }

  /**
   * 根据索引获取数据项
   */
  getDataItem(index: number) {
    return this._data[index];
  }

  /**
   * 是否有效的索引
   */
  isValidIndex(index: number) {
    return typeof this._data[index] !== 'undefined';
  }

  // eslint-disable-next-line class-methods-use-this
  getMinIndex(range = true) {
    if (range === true) {
      return this._indexRange[0];
    }

    return 0;
  }

  getMaxIndex(range = true) {
    if (range === true) {
      return this._indexRange[1] - 1;
    }

    return this._data.length - 1;
  }

  /**
   * 是否在最小索引处
   */
  isIndexMin(index = this._currentIndex, range = true) {
    return index <= this.getMinIndex(range);
  }

  /**
   * 是否在最大索引处
   */
  isIndexMax(index = this._currentIndex, range = true) {
    return index >= this.getMaxIndex(range);
  }

  /**
   * 获取当前数据索引
   */
  getCurrentIndex() {
    return this._currentIndex;
  }

  /**
   * 设置当前数据索引
   * ! 限制在 IndexRange 区间中
   */
  private __setCurrentIndex(index: number) {
    this._currentIndex = Math.min(Math.max(this.getMinIndex(true), index), this.getMaxIndex(true));
  }

  /**
   * 是否有区间限制
   */
  isRange() {
    return this._isRange;
  }

  /**
   * 获取数据索引区间, 开闭 [,)
   */
  getIndexRange(): [number, number] {
    // deepClone
    return [...this._indexRange];
  }

  /**
   * 设置数据索引区间, 开闭 [,)
   */
  private __setIndexRange(indexRange: [number, number]) {
    if (!this.isRange()) {
      return;
    }
    if (indexRange[0] > indexRange[1]) {
      throw new Error(`indexRange[1] must be greater than indexRange[0].`);
    }

    // 先确定上限
    this._indexRange[1] = Math.min(
      Math.max(this.getMinIndex(false), indexRange[1]),
      this.getMaxIndex(false) + 1
    );

    this._indexRange[0] = Math.min(
      Math.max(this.getMinIndex(false), indexRange[0]),
      this._indexRange[1]
    );
  }

  /**
   * 是否是范围内的有效索引
   */
  isInRangeIndex(index: number) {
    return this.isValidIndex(index) && this.getMinIndex() <= index && index <= this.getMaxIndex();
  }

  /**
   * 更新状态
   */
  update({ currentIndex, indexRange }: { currentIndex?: number; indexRange?: [number, number] }) {
    //
    if (typeof indexRange !== 'undefined') {
      this.__setIndexRange(indexRange);
    }
    if (typeof currentIndex !== 'undefined') {
      this.__setCurrentIndex(currentIndex);
    }

    if (this.isRange() && typeof indexRange !== 'undefined') {
      // @ts-ignore
      this.timeline._dispatchEvent(EVENT_TYPE_MAP.CHANGE_RANGE, {
        index: this.getCurrentIndex(),
        indexRange: this.getIndexRange()
      });
    }
  }

  //
}
